<template>
  <div style="max-width: 960px; margin: 0 auto; padding: 1.25rem;">

    <!-- Header -->
    <div style="margin-bottom: 1.25rem;">
      <h1 style="font-size: 1.5rem; font-weight: 700; color: #111827; margin: 0 0 0.25rem;">Completed Deposits</h1>
      <p style="font-size: 0.875rem; color: #6B7280; margin: 0;">
        {{ depositsData.total ? depositsData.total.toLocaleString() : '0' }} deposits found
      </p>
    </div>

    <!-- Summary stats -->
    <div style="display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ depositsData.total ? depositsData.total.toLocaleString() : '0' }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Results</div>
      </div>
      <div style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ deposits.length }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Loaded</div>
      </div>
      <div v-if="filterCount > 0" style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #FDF2F8; border: 1px solid #FBCFE8; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #BE185D;">{{ filterCount }}</div>
        <div style="font-size: 0.6875rem; color: #9D174D; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Filters Active</div>
      </div>
    </div>

    <!-- Search & Action bar -->
    <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.25rem; padding: 0.875rem 1rem; background: #FAFAFA; border: 1px solid #E5E7EB; border-radius: 0.625rem;">
      <div style="display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 200px;">
        <div style="position: relative; flex: 1;">
          <svg xmlns="http://www.w3.org/2000/svg" style="position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); width: 0.875rem; height: 0.875rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" v-model="searchTerm" v-on:keyup.enter="search" placeholder="Search by ID, bank deposit ID, notes..." style="width: 100%; padding: 0.5rem 0.75rem 0.5rem 2.25rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; outline: none;" @focus="$event.target.style.borderColor='#EC4899'" @blur="$event.target.style.borderColor='#D1D5DB'" />
        </div>
        <button @click="search" style="padding: 0.5rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer; white-space: nowrap;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">Search</button>
        <button @click="openFilterModal = true" style="padding: 0.5rem 1rem; background: #374151; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 500; cursor: pointer; white-space: nowrap; position: relative;" @mouseover="$event.target.style.background='#1F2937'" @mouseout="$event.target.style.background='#374151'">
          Filter
          <span v-if="filterCount > 0" style="position: absolute; top: -0.375rem; right: -0.375rem; width: 1.125rem; height: 1.125rem; background: #EC4899; color: white; border-radius: 50%; font-size: 0.625rem; font-weight: 700; display: flex; align-items: center; justify-content: center;">{{ filterCount }}</span>
        </button>
      </div>
    </div>

    <!-- Results info -->
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; padding: 0 0.25rem;">
      <span style="font-size: 0.75rem; color: #9CA3AF;">Showing {{ deposits.length }} of {{ depositsData.total || 0 }}</span>
    </div>

    <!-- Empty state -->
    <div v-if="deposits.length === 0" style="text-align: center; padding: 3rem 1rem; color: #9CA3AF; background: #FAFAFA; border: 1.5px dashed #D1D5DB; border-radius: 0.75rem;">
      <p style="font-size: 0.9375rem; margin-bottom: 0.25rem; color: #6B7280; font-weight: 500;">No deposits found</p>
      <p style="font-size: 0.8125rem;">Try adjusting your search or filters.</p>
    </div>

    <!-- Deposit list -->
    <div v-else style="display: flex; flex-direction: column; gap: 0.5rem;">
      <div
        v-for="deposit in deposits"
        :key="deposit.id"
        style="border-radius: 0.625rem; overflow: visible; box-shadow: 0 1px 3px rgba(0,0,0,0.08); border: 1px solid #E5E7EB; position: relative;"
      >
        <div
          style="display: flex; align-items: center; padding: 0.75rem 1rem; gap: 0.875rem; background: white; border-left: 4px solid #10B981;"
          @mouseover="$event.currentTarget.style.background='#FAFAFA'"
          @mouseout="$event.currentTarget.style.background='white'"
        >
          <!-- Deposit icon -->
          <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; border-radius: 0.625rem; background: #ECFDF5; display: flex; align-items: center; justify-content: center;">
            <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #10B981;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          </div>

          <!-- Info -->
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
              <span style="font-size: 0.9375rem; font-weight: 700; color: #111827;">
                Deposit #{{ deposit.id }}
              </span>
              <span v-if="deposit.bank_deposit_identifier" style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #EFF6FF; color: #3B82F6; border-radius: 9999px; font-weight: 600;">
                Bank: {{ deposit.bank_deposit_identifier }}
              </span>
              <span v-if="deposit.donation_count" style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #F3F4F6; color: #6B7280; border-radius: 9999px; font-weight: 500;">
                {{ deposit.donation_count }} donation{{ deposit.donation_count != 1 ? 's' : '' }}
              </span>
            </div>
            <!-- Meta row -->
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-top: 0.25rem; flex-wrap: wrap;">
              <span v-if="deposit.name" style="font-size: 0.75rem; color: #6B7280;">{{ deposit.name }}</span>
              <span v-if="deposit.notes" style="font-size: 0.75rem; color: #9CA3AF; display: flex; align-items: center; gap: 0.25rem;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.6875rem; height: 0.6875rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                {{ truncate(deposit.notes, 40) }}
              </span>
            </div>
          </div>

          <!-- Amount & Dates -->
          <div style="text-align: right; flex-shrink: 0;">
            <div style="font-size: 1.125rem; font-weight: 700; color: #111827;">${{ formatAmount(deposit.deposit_total) }}</div>
            <div style="font-size: 0.6875rem; color: #9CA3AF;">Created: {{ formatDate(deposit.created_at) }}</div>
            <div v-if="deposit.closed_date" style="font-size: 0.6875rem; color: #9CA3AF;">Closed: {{ formatDate(deposit.closed_date) }}</div>
          </div>

          <!-- Actions -->
          <div style="flex-shrink: 0;">
            <completed-deposit-crud-button :deposit="deposit" :key="deposit.id"></completed-deposit-crud-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More -->
    <div v-if="next_page_url" style="text-align: center; margin-top: 1.5rem;">
      <button @click="loadMore" id="loadMore" style="padding: 0.625rem 2rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;" @mouseover="$event.target.style.borderColor='#EC4899'; $event.target.style.color='#EC4899'" @mouseout="$event.target.style.borderColor='#D1D5DB'; $event.target.style.color='#374151'">
        Load More ({{ depositsData.to }} of {{ depositsData.total }})
      </button>
    </div>

    <!-- Filter Modal -->
    <transition enter-class="transition ease-out duration-100 transform" enter-active-class="opacity-0" enter-to-class="opacity-100" leave-class="transition ease-in duration-200 transform" leave-active-class="opacity-100" leave-to-class="opacity-0">
      <div v-if="openFilterModal" class="fixed z-50 inset-0 overflow-y-auto" id="deposit-search-modal">
        <div class="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true"><div class="absolute inset-0 bg-gray-500 opacity-75"></div></div>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div style="border-radius: 0.75rem; max-width: 28rem;" class="inline-block align-bottom bg-white px-6 pt-6 pb-5 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full" role="dialog" aria-modal="true">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem;">
              <h3 style="font-size: 1.125rem; font-weight: 700; color: #111827;">Filter Deposits</h3>
              <button @click="openFilterModal = false" style="padding: 0.375rem; color: #9CA3AF; background: none; border: none; cursor: pointer; border-radius: 0.25rem;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div style="display: flex; flex-direction: column; gap: 1rem;">
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Deposit ID</label>
                <input v-model="depositId" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Bank Deposit ID</label>
                <input v-model="bankDepositId" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Date Range</label>
                <flat-pickr v-model="date" :config="{mode: 'range'}" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;"></flat-pickr>
              </div>
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Amount ($)</label>
                <input v-model="amount" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.25rem;">
              <button @click="resetFilters" style="padding: 0.5rem 1rem; background: white; color: #6B7280; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 500; cursor: pointer;">Reset</button>
              <button @click="search" style="padding: 0.5rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">Apply Filters</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import moment from "moment";
import flatPickr from 'vue-flatpickr-component';

export default {
  props: {
    allDeposits: {},
  },
  components: { flatPickr },
  data() {
    return {
      depositsData: this.allDeposits,
      deposits: this.allDeposits?.data,
      openFilterModal: false,
      next_page_url: this.allDeposits?.next_page_url ? '/admin/deposit-search-with-term?page=2' : '',
      searchTerm: '',
      depositId: '',
      bankDepositId: '',
      amount: '',
      date: '',
      filterCount: 0,
    }
  },
  methods: {
    formatDate(date) { return moment(date).format('MM-DD-YYYY'); },
    formatAmount(cents) {
      if (!cents && cents !== 0) return '0.00';
      return (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.substring(0, len) + '...' : str;
    },
    getFilterRequestQuery() {
      this.filterCount = 0;
      let q = '';
      if (this.depositId) { q += '&depositId=' + this.depositId; this.filterCount++; }
      if (this.bankDepositId) { q += '&bankDepositId=' + this.bankDepositId; this.filterCount++; }
      if (this.amount) { q += '&amount=' + this.amount; this.filterCount++; }
      if (this.date) { q += '&date=' + this.date; this.filterCount++; }
      return q;
    },
    resetFilters() {
      this.depositId = ''; this.bankDepositId = ''; this.amount = ''; this.date = '';
      this.filterCount = 0;
    },
    search() {
      axios.get('/admin/deposit-search-with-term?search_term=' + this.searchTerm + this.getFilterRequestQuery())
        .then(response => {
          this.deposits = response.data.data;
          this.depositsData = response.data;
          this.next_page_url = response.data.next_page_url;
          this.openFilterModal = false;
        });
    },
    loadMore() {
      if (this.next_page_url) {
        axios.get(this.next_page_url + '&search_term=' + this.searchTerm + this.getFilterRequestQuery()).then(response => {
          this.depositsData = response.data;
          response.data.data.forEach(v => this.deposits.push(v));
          this.next_page_url = response.data.next_page_url;
        });
      }
    },
  },
}
</script>
